import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import * as wikiApi from '@/features/wiki/api';
import { WikiItemRef } from '@/features/wiki/types';
import { useAppData } from '@/app-data';
import { taskPath } from '@/constants/routes';

/**
 * "Related items" on a wiki page: board items linked to this page (DERIVED). `source: 'wiki'` = this
 * page embeds the item (an item card); `source: 'item'` = the item's description links this page.
 * Refetches when `refreshKey` changes (a page save re-derives the embed→item refs).
 */
export default function WikiRelatedItems({ pageId, refreshKey }: { pageId: string; refreshKey?: unknown }) {
  const { boards } = useAppData();
  const boardById = useMemo(() => new Map(boards.map((b) => [b.id, b])), [boards]);
  const [items, setItems] = useState<WikiItemRef[]>([]);

  useEffect(() => {
    let cancelled = false;
    wikiApi
      .listWikiPageItems(pageId)
      .then((i) => !cancelled && setItems(i))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [pageId, refreshKey]);

  if (!items.length) return null;

  return (
    <div className="wiki-col mt-8 border-t border-line pb-10 pt-5">
      <div className="mb-2.5 text-[11px] font-semibold uppercase tracking-wide text-fg-faint">Related items · {items.length}</div>
      <div className="flex flex-col gap-1.5">
        {items.map((it) => {
          const b = boardById.get(it.board_id);
          const inner = (
            <>
              <span className="item-key shrink-0 tnum text-[11px] font-semibold">{it.key}</span>
              <span className="min-w-0 flex-1 truncate text-[13px] text-fg-soft">{it.title || 'Untitled'}</span>
              <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-fg-faint">{it.source === 'wiki' ? 'embedded' : 'mentions'}</span>
            </>
          );
          const cls = 'flex items-center gap-2.5 rounded-lg border border-line bg-surface-raised px-2.5 py-1.5 no-underline transition-colors hover:border-line-strong hover:bg-layer';
          return b ? (
            <Link key={`${it.item_id}-${it.source}`} to={taskPath(b.slug, it.key)} className={cls} title={it.title}>
              {inner}
            </Link>
          ) : (
            <div key={`${it.item_id}-${it.source}`} className={cls} title={it.title}>
              {inner}
            </div>
          );
        })}
      </div>
    </div>
  );
}
